import React, { useState } from 'react';
import { Send, ArrowRightLeft, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';
import Modal from './ui/Modal';

interface TransferUnit {
  id: string;
  name: string;
}

interface TransferModalProps {
  units: TransferUnit[];
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TransferModal: React.FC<TransferModalProps> = ({ units, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email for the destination account.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await apiService.createTransfer(units.map((u) => u.id), trimmed);
    setSubmitting(false);
    if (res.success) {
      onSuccess(
        `Request sent to ${trimmed}. The transfer completes once the recipient accepts it.`
      );
    } else {
      setError(res.message || 'Could not create the transfer.');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Transfer ${units.length === 1 ? 'lock' : `${units.length} locks`}`}
      icon={<ArrowRightLeft className="h-4 w-4 text-indigo-500" />}
      headerAccent="bg-indigo-50/50"
      maxWidth="md"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-60"
          >
            {submitting ? (
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send request
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Locks to transfer */}
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            {units.length === 1 ? 'Lock' : 'Locks'}
          </p>
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
            {units.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
              >
                {u.name}
              </span>
            ))}
          </div>
        </div>

        {/* Destination email */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Destination account email
          </label>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => e.key === 'Enter' && !submitting && handleSubmit()}
            placeholder="user@example.com"
            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
          />
        </div>

        {/* Acceptance note */}
        <div className="flex gap-2 text-xs text-slate-500 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-2.5">
          <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-px" />
          <span>
            The recipient must <strong>accept</strong> the request for the ownership change to take
            effect. On acceptance, previously shared access is revoked and the lock leaves your
            account.
          </span>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TransferModal;
